import {inject, Injectable, Injector} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {Decimal} from 'decimal.js';
import {combineLatest, combineLatestWith, defer, filter, Observable, of, switchMap} from 'rxjs';
import {map, share, take, tap} from 'rxjs/operators';
import appConfig from '../../../config/app-config';
import {isDevOrCiEnv} from '../../../services/config.service';
import {DEX_RATE_REF_SYMBOL} from '../dex-streaming/const';
import {DexMdSubjectService} from '../dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../dex-streaming/subjects/dex-securities-subject-service';
import {ComputedTranche} from '../dex-streaming/types/computed/computed-tranche';
import {Deliverable, DeliverableId} from '../dex-streaming/types/deliverable';
import {RefRate} from '../dex-streaming/types/ref-rate';
import {mathValueWithDelDecJs, mathWithDiffDpDecimalJs, numToV, ValueWithDel, vToDec} from '../dex-streaming/utils';
import {Erc20BalancesSharedService} from '../dyn-balances/erc20-balances-shared.service';

export interface RefBalance {
    balance: bigint;
    balanceDel: Deliverable;
    rate: bigint;
    rateDel: Deliverable;
    refBalance: bigint;
    refBalanceDel: Deliverable;
    mathComments?: string;
}

interface RefRateWithDec extends RefRate {
    decPrice: Decimal;
}

interface ResolvedRefRate {
    baseDeliverable: Deliverable;
    quoteDeliverable: Deliverable;
    price: bigint | undefined;
    decPrice: Decimal | undefined;
}

@Injectable()
export class TrancheDelRefBalanceService {

    private readonly erc20BalancesSharedService = inject(Erc20BalancesSharedService);
    private readonly dexMdSubjectService = inject(DexMdSubjectService);
    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);

    private refRateQuoteDel: Deliverable;
    private readonly refQuoteDelReq$ = this.createRefQuoteDelReq();

    constructor() {

        // TODO
        if (isDevOrCiEnv(appConfig.env)) {
            (window as any).dexLogRefRates = (pairOrBase?: string) => {
                const map = new Map<string, string>();

                const arr = [...this.dexMdSubjectService.refRateMapByPair.values()]
                    .map(r => {
                        const dBase = this.dexSecuritiesSubjectService.getDeliverableById(r.baseDeliverableId);
                        const dQuote = this.dexSecuritiesSubjectService.getDeliverableById(r.quoteDeliverableId);
                        const decPrice = vToDec(r.price, dQuote.decimalPlaces);

                        map.set(`${dBase.symbol}/${dQuote.symbol}`, decPrice.toFixed());

                        return `${dBase.symbol}/${dQuote.symbol}=${decPrice.toFixed()}`;
                    })
                    .sort();

                if (pairOrBase) {
                    if (pairOrBase.includes('/')) {
                        return arr.find(it => it.startsWith(`${pairOrBase}=`));
                    } else {
                        return arr.filter(it => {
                            const [itPair] = it.split('=');

                            return itPair.split('/').includes(pairOrBase);
                        });
                    }
                }

                return arr;
            };
        }
    }

    public getTrancheRateStream(tranche: ComputedTranche, injector: Injector): Observable<ValueWithDel> {
        return toObservable(tranche.$unitPrice, {injector}).pipe(
            map(rate => ({
                v: rate,
                d: tranche.unitPriceDeliverable
            }))
        );
    }

    public getRefCrossRate(baseDel: Deliverable, refDel: Deliverable): Observable<ResolvedRefRate> {

        // Base/Quote
        const getDirectRate = () =>
            this.getRefRateStream(baseDel.deliverableId, refDel.deliverableId, true).pipe(
                map(rate => ({
                    baseDeliverable:  baseDel,
                    quoteDeliverable: refDel,
                    price:            rate && BigInt(rate.price),
                    decPrice:         rate && vToDec(rate.price, refDel.decimalPlaces)
                } satisfies ResolvedRefRate))
            );

        // 1 div Base/Quote
        const getDirectReverseRate = () =>
            this.getRefRateStream(refDel.deliverableId, baseDel.deliverableId, true).pipe(
                map(rate => {
                    const result: ResolvedRefRate = {
                        baseDeliverable:  baseDel,
                        quoteDeliverable: refDel,
                        price:            undefined,
                        decPrice:         undefined
                    };

                    if (rate) {
                        const decDirectPrice = vToDec(rate.price, baseDel.decimalPlaces);
                        const decReversePrice = new Decimal(1).div(decDirectPrice);

                        result.price = numToV(decReversePrice, refDel.decimalPlaces);
                        result.decPrice = decReversePrice;
                    }

                    return result;
                })
            );

        // Base/USD
        const getDirectRateToRefQuote = () =>
            this.getRefRateToRefQuote(baseDel).pipe(
                map(rate => ({
                    baseDeliverable:  baseDel,
                    quoteDeliverable: refDel,
                    price:            rate && BigInt(rate.price),
                    decPrice:         rate && vToDec(rate.price, refDel.decimalPlaces)
                } satisfies ResolvedRefRate))
            );

        // Base -> USD -> Quote
        const getCrossRate = () =>
            combineLatest({
                baseRate: this.getRefRateToRefQuote(baseDel),
                quoteRate: this.getRefRateToRefQuote(refDel)
            }).pipe(
                map(({baseRate, quoteRate}) => {
                    const decCrossPrice = (baseRate && quoteRate) && baseRate.decPrice.div(quoteRate.decPrice);
                    const crossPrice = decCrossPrice && numToV(decCrossPrice, refDel.decimalPlaces);

                    return {
                        baseDeliverable:  baseDel,
                        quoteDeliverable: refDel,
                        price:            crossPrice,
                        decPrice:         decCrossPrice
                    } satisfies ResolvedRefRate;
                })
            );

        // Base/Base = 1
        const getEqualRate = () => of<ResolvedRefRate>({
            baseDeliverable:  baseDel,
            quoteDeliverable: baseDel,
            price:            numToV(1, baseDel.decimalPlaces),
            decPrice:         new Decimal(1)
        } satisfies ResolvedRefRate);

        return baseDel.deliverableId === refDel.deliverableId
            ? getEqualRate()
            : this.dexMdSubjectService.getRefRateByPair(baseDel.deliverableId, refDel.deliverableId) // Base/Quote
                ? getDirectRate()
                : this.dexMdSubjectService.getRefRateByPair(refDel.deliverableId, baseDel.deliverableId) // Quote/Base
                    ? getDirectReverseRate()
                    : this.resolveRefQuoteDel().pipe(
                        switchMap(() =>
                            (refDel.deliverableId === this.refRateQuoteDel.deliverableId) // Base/USD
                                ? getDirectRateToRefQuote()
                                : getCrossRate()
                        )
                    );
    }

    public getRefCrossBalance(amount: ValueWithDel, refDel: Deliverable): Observable<RefBalance> {
        return this.getRefCrossRate(amount.d, refDel).pipe(
            map(crossRate => {
                const decAmount = vToDec(amount.v, amount.d.decimalPlaces);
                const refBalance = crossRate.decPrice
                    && numToV(decAmount.mul(crossRate.decPrice), refDel.decimalPlaces);

                return {
                    balance: amount.v,
                    balanceDel: amount.d,
                    rate: crossRate.price,
                    rateDel: refDel,
                    refBalance,
                    refBalanceDel: refDel
                } satisfies RefBalance;
            })
        );
    }

    public getRefCrossBalanceExcludingFee(
        amount: ValueWithDel,
        refDel: Deliverable,
        feeUsd: ValueWithDel
    ): Observable<RefBalance> {
        return this.resolveRefQuoteDel().pipe(
            switchMap(() => {
                const decAmount = vToDec(amount.v, amount.d.decimalPlaces);
                const amountStr = decAmount.toFixed();
                const amountSymbol = amount.d.symbol;
                const refSymbol = refDel.symbol;
                const refDp = refDel.decimalPlaces;
                const feeStr = vToDec(feeUsd.v, feeUsd.d.decimalPlaces).toFixed();

                return combineLatest({
                    rateToUsd:          this.getRefCrossRate(amount.d, this.refRateQuoteDel),
                    rateFromUsdReverse: this.getRefCrossRate(refDel, this.refRateQuoteDel)
                }).pipe(
                    map(({rateToUsd, rateFromUsdReverse}) => {
                        const result: RefBalance = {
                            balance:       amount.v,
                            balanceDel:    amount.d,
                            rate:          undefined,
                            rateDel:       refDel,
                            refBalance:    undefined,
                            refBalanceDel: refDel
                        };

                        const mathCommentsParts: string[] = [
                            `Estimating ${amountStr} ${amountSymbol} -> ${refSymbol}:`
                        ];

                        if (rateToUsd?.decPrice && rateFromUsdReverse?.decPrice) {
                            let decUsd = decAmount.mul(rateToUsd.decPrice);
                            const decCrossRate = rateToUsd.decPrice.mul(rateFromUsdReverse.decPrice);

                            result.rate = numToV(decCrossRate, refDp);

                            mathCommentsParts.push(
                                `Rate ${amountSymbol}/USD = ${rateToUsd.decPrice.toFixed()}`,
                                `${amountStr} ${amountSymbol} = ${decUsd.toFixed()} USD`
                            );

                            if (feeUsd?.v) {
                                decUsd = decUsd.sub(vToDec(feeUsd.v, feeUsd.d.decimalPlaces));

                                mathCommentsParts.push(
                                    `Fee: ${feeStr} USD`,
                                    `After the fee: ${decUsd.toFixed()} USD`);
                            }

                            result.refBalance = numToV(decUsd.div(rateFromUsdReverse.decPrice), refDp);

                            mathCommentsParts.push(
                                `Rate ${refSymbol}/USD = ${rateFromUsdReverse.decPrice.toFixed()}`,
                                `${decUsd.toFixed()} USD = ${vToDec(result.refBalance, refDp)} ${refSymbol}`
                            );
                        } else {
                            mathCommentsParts.push(`No rates for ${amountSymbol} → ${refSymbol}`);
                        }

                        result.mathComments = mathCommentsParts.join('\n    ');

                        return result;
                    })
                );
            })
        );
    }

    public getRefValue(amount: ValueWithDel, rate: ValueWithDel, refDel: Deliverable): ValueWithDel {
        return mathValueWithDelDecJs(
            amount,
            rate,
            refDel,
            (a, r) => a.mul(r)
        );
    }

    public getReverseRefValue(amount: ValueWithDel, rate: ValueWithDel, refDel: Deliverable): ValueWithDel {
        return mathValueWithDelDecJs(
            amount,
            rate,
            refDel,
            (a, r) => r.isZero() ? new Decimal(0) : a.div(r)
        );
    }

    public getTrancheRefBalanceStream(
        tranche: ComputedTranche,
        accountAddr: string,
        injector: Injector
    ): Observable<RefBalance> {
        return toObservable(tranche.$totalValueDeliverable, {injector}).pipe(
            combineLatestWith(
                toObservable(tranche.$unitPrice, {injector}),
                this.erc20BalancesSharedService.getDelBalanceStream(accountAddr, tranche.unitDeliverable)
            ),
            map(([refBalanceDel, rate, dBalance]) => {
                const balance = dBalance.balance;
                const balanceDel = dBalance.deliverable;
                const rateDel = tranche.unitPriceDeliverable;

                const refBalance = mathWithDiffDpDecimalJs(
                    balance,
                    balanceDel.decimalPlaces,
                    rate,
                    rateDel.decimalPlaces,
                    refBalanceDel.decimalPlaces,
                    (b, r) => b.mul(r)
                );

                return {
                    balance,
                    balanceDel,
                    rate,
                    rateDel,
                    refBalance,
                    refBalanceDel
                };
            })
        );
    }

    public getRefBalanceStream(
        del: Deliverable,
        accountAddr: string
    ): Observable<RefBalance> {
        return this.getRefRateToRefQuote(del).pipe(
            switchMap(refRate => {
                const balanceDel = del;

                const balance$ = this.erc20BalancesSharedService.getDelBalanceStream(accountAddr, balanceDel).pipe(
                    map(r => r.balance)
                );

                const refBalanceDel = this.refRateQuoteDel;
                const rateDel = refBalanceDel;
                const rate = refRate && BigInt(refRate.price);
                const refBalance$ = balance$.pipe(
                    map(balance =>
                        (balance && rate) && mathWithDiffDpDecimalJs(
                            balance,
                            balanceDel.decimalPlaces,
                            rate,
                            rateDel.decimalPlaces,
                            refBalanceDel.decimalPlaces,
                            (b, r) => b.mul(r)
                        )
                    )
                );

                return refBalance$.pipe(
                    combineLatestWith(balance$)
                ).pipe(
                    map(([refBalance, balance]) => ({
                        balance,
                        balanceDel,
                        rate,
                        rateDel,
                        refBalance,
                        refBalanceDel
                    }))
                );
            })
        );
    }

    private createRefQuoteDelReq(): Observable<Deliverable> {
        return this.dexSecuritiesSubjectService.getDeliverableStreamBySymbol(DEX_RATE_REF_SYMBOL).pipe(
            filter(Boolean),
            take(1),
            tap(d => this.refRateQuoteDel = d),
            share()
        );
    }

    private resolveRefQuoteDel(): Observable<Deliverable> {
        return defer(() =>
            this.refRateQuoteDel
                ? of(this.refRateQuoteDel)
                : this.refQuoteDelReq$
        );
    }

    private getRefRateToRefQuote(baseDel: Deliverable): Observable<RefRateWithDec | undefined> {
        return this.resolveRefQuoteDel().pipe(
            switchMap(() =>
                baseDel.deliverableId === this.refRateQuoteDel.deliverableId
                    ? of<RefRateWithDec>({
                        baseDeliverableId:  baseDel.deliverableId,
                        from:               null,
                        price:              String(numToV(1, baseDel.decimalPlaces)),
                        priceSourceType:    undefined,
                        quoteDeliverableId: baseDel.deliverableId,
                        timestamp:          null,
                        decPrice:           new Decimal(1)
                    } satisfies RefRateWithDec)

                    : this.getRefRateStream(
                        baseDel.deliverableId,
                        this.refRateQuoteDel.deliverableId,
                        true
                    )
            )
        );
    }

    private getRefRateStream(
        dBaseId: DeliverableId,
        dQuoteId: DeliverableId,
        emitEmpty: boolean = false
    ): Observable<RefRateWithDec | undefined> {
        let priceDp: number;

        return this.dexMdSubjectService.getRefRateStream(dBaseId, dQuoteId, emitEmpty).pipe(
            map(r => {
                priceDp ??= this.dexSecuritiesSubjectService.getDeliverableById(dQuoteId)?.decimalPlaces;

                return {
                    ...r,
                    decPrice: vToDec(r.price, priceDp)
                } satisfies RefRateWithDec;
            })
        );
    }

}
